// Provider names (aligned with backend route metrics)
export type TProviderName =
  "Google" | "Osrm" | "Mapbox" | "Here" | "Esri" | "TomTom";

export const ROUTE_CACHE_METRIC_PROVIDERS: readonly TProviderName[] = [
  "Google",
  "Osrm",
  "Mapbox",
  "Here",
  "Esri",
  "TomTom",
] as const;

// Metric suffixes
export type TRouteMetricSuffix =
  | "Total"
  | "Cache"
  | "CacheOptimized"
  | "CacheNotOptimized"
  | "NotCached"
  | "Call"
  | "FetchOptimized"
  | "FetchNotOptimized";
export type TDistanceMetricSuffix = "Total" | "Cache" | "NotCached" | "Cached";
export type TStationDistancesMetricSuffix = "" | "InnerIteration";
export type TGeneralMetricSuffix =
  "TotalCalls" | "OsrmFirstFlowCalls" | "NonOsrmFirstFlowCalls";

export type TRouteMetricKey<T extends TProviderName> =
  `getRoute${T}${TRouteMetricSuffix}`;

export type TDistanceMetricKey<T extends TProviderName> =
  `getDistance${T}${TDistanceMetricSuffix}`;

export type TStationDistancesMetricKey<T extends TProviderName> =
  `getStationDistances${T}${TStationDistancesMetricSuffix}`;

export type TGeneralMetricKey =
  | `getRoute${TGeneralMetricSuffix}`
  | `getDistance${TGeneralMetricSuffix}`
  | "getRouteOsrmFirstFlowCalls"
  | "getRouteNonOsrmFirstFlowCalls";

export type TGoogleSpecificMetric =
  "savedMLRouteUsedForReorder" | "savedNotMLRouteUsedForReorder";

export type TOtherSpecificMetric =
  | "savedNotOptimizedRoute"
  | "getStationMatrixCached"
  | "stationRouteUpdated"
  | "stationRouteCreatedOptimized"
  | "stationRouteCreatedNotOptimized";

// All metric keys
export type TAllMetricKeys =
  | TRouteMetricKey<TProviderName>
  | TDistanceMetricKey<TProviderName>
  | TStationDistancesMetricKey<TProviderName>
  | TGeneralMetricKey
  | TGoogleSpecificMetric
  | TOtherSpecificMetric;

export type TMetrics = {
  startedAtTimestamp: number;
} & {
  [K in TAllMetricKeys]: number;
};

export type TRouteCacheMetricsSummary = {
  cacheSum: number;
  totalSum: number;
  /** 0–100; 0 when total is 0 */
  cachePercent: number;
};

const emptyRouteCacheMetricsSummary = (): TRouteCacheMetricsSummary => ({
  cacheSum: 0,
  totalSum: 0,
  cachePercent: 0,
});

const withCachePercent = (
  cacheSum: number,
  totalSum: number,
): TRouteCacheMetricsSummary => ({
  cacheSum,
  totalSum,
  cachePercent: totalSum === 0 ? 0 : Math.round((cacheSum / totalSum) * 100),
});

type TProviderCacheKeyResolver = (provider: TProviderName) => {
  cacheKeys: (keyof TMetrics)[];
  totalKey: keyof TMetrics;
};

const sumProviderCacheMetrics = (
  metrics: Partial<TMetrics> | null | undefined,
  resolveKeys: TProviderCacheKeyResolver,
): TRouteCacheMetricsSummary => {
  if (!metrics) {
    return emptyRouteCacheMetricsSummary();
  }
  let cacheSum = 0;
  let totalSum = 0;
  for (const provider of ROUTE_CACHE_METRIC_PROVIDERS) {
    const { cacheKeys, totalKey } = resolveKeys(provider);
    for (const cacheKey of cacheKeys) {
      cacheSum += Number(metrics[cacheKey] ?? 0);
    }
    totalSum += Number(metrics[totalKey] ?? 0);
  }
  return withCachePercent(cacheSum, totalSum);
};

/** Sum route Cache/Total across all providers — provider-agnostic cache hit rate. */
export const sumRouteCacheMetrics = (
  metrics?: Partial<TMetrics> | null,
): TRouteCacheMetricsSummary =>
  sumProviderCacheMetrics(metrics, provider => ({
    cacheKeys: [`getRoute${provider}Cache` as keyof TMetrics],
    totalKey: `getRoute${provider}Total` as keyof TMetrics,
  }));

/**
 * Sum distance Cache|Cached / Total across all providers.
 * Providers emit either `Cache` (Google/Here/Esri) or `Cached` (Osrm/Mapbox/TomTom).
 * Includes getStationMatrixCached so produce logs match matrix preload usage.
 */
export const sumDistanceCacheMetrics = (
  metrics?: Partial<TMetrics> | null,
): TRouteCacheMetricsSummary => {
  const providerSummary = sumProviderCacheMetrics(metrics, provider => ({
    cacheKeys: [
      `getDistance${provider}Cache` as keyof TMetrics,
      `getDistance${provider}Cached` as keyof TMetrics,
    ],
    totalKey: `getDistance${provider}Total` as keyof TMetrics,
  }));
  const matrixCached = Number(metrics?.getStationMatrixCached ?? 0);
  if (!matrixCached) {
    return providerSummary;
  }
  return withCachePercent(
    providerSummary.cacheSum + matrixCached,
    providerSummary.totalSum + matrixCached,
  );
};

/** True when metric key is a provider route/distance Cache counter (for UI highlighting). */
export const isRouteOrDistanceCacheMetricKey = (key: string): boolean =>
  /^get(Route|Distance)(Google|Osrm|Mapbox|Here|Esri|TomTom)Cache$/.test(key);
